import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import { theme } from '../utils/theme';

const SuggestionPictogramManager = ({ isAdmin = false }) => {
  const { t } = useTranslation('promptEditor');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState({
    name: '',
    icon: ''
  });

  // Generate key from name
  const generateKey = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  };

  // Fetch all suggestions
  const fetchSuggestions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const suggestionsResponse = await api.get('/api/content/suggestion-pictograms');
      setSuggestions(suggestionsResponse.data.suggestions || []);
      
      setIsLoading(false);
    } catch (err) {
      setError(t('suggestions.error.load'));
      setIsLoading(false);
      console.error('Error fetching data:', err);
    }
  };

  // Create new suggestion
  const createSuggestion = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const key = generateKey(currentSuggestion.name);
      const suggestionData = {
        key,
        name: currentSuggestion.name,
        icon: `/card-icons/${currentSuggestion.icon}`
      };
      
      await api.post('/api/admin/suggestion-pictograms', suggestionData);
      await fetchSuggestions();
      resetForm();
    } catch (err) {
      setError(t('suggestions.error.create'));
      setIsLoading(false);
      console.error('Error creating suggestion:', err);
    }
  };

  // Update existing suggestion
  const updateSuggestion = async (originalKey) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const suggestionData = {
        key: originalKey, // Keep original key to maintain references
        name: currentSuggestion.name,
        icon: `/card-icons/${currentSuggestion.icon}`
      };
      
      await api.put(`/api/admin/suggestion-pictograms/${originalKey}`, suggestionData);
      await fetchSuggestions();
      resetForm();
    } catch (err) {
      setError(t('suggestions.error.update'));
      setIsLoading(false);
      console.error('Error updating suggestion:', err);
    }
  };

  // Delete a suggestion
  const deleteSuggestion = async (suggestion) => {
    if (window.confirm(t('suggestions.confirmDelete'))) {
      try {
        setIsLoading(true);
        setError(null);
        await api.delete(`/api/admin/suggestion-pictograms/${suggestion.id}`);
        await fetchSuggestions();
      } catch (err) {
        setError(t('suggestions.error.delete'));
        setIsLoading(false);
        console.error('Error deleting suggestion:', err);
      }
    }
  };

  // Reset the form
  const resetForm = () => {
    setCurrentSuggestion({
      name: '',
      icon: ''
    });
    setShowForm(false);
    setIsLoading(false);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!currentSuggestion.name || !currentSuggestion.icon) {
      setError(t('suggestions.error.missingFields'));
      return;
    }

    // Ensure the icon filename ends with .svg
    const icon = currentSuggestion.icon.endsWith('.svg') 
      ? currentSuggestion.icon 
      : `${currentSuggestion.icon}.svg`;
    
    setCurrentSuggestion(prev => ({
      ...prev,
      icon: icon
    }));
    
    if (currentSuggestion.originalKey) {
      updateSuggestion(currentSuggestion.originalKey);
    } else {
      createSuggestion();
    }
  };

  // Edit a suggestion
  const editSuggestion = (suggestion) => {
    setCurrentSuggestion({
      name: suggestion.name,
      icon: suggestion.icon.split('/').pop(), // Get just the filename
      originalKey: suggestion.key // Keep track of original key for update
    });
    setShowForm(true);
  };

  // Initialize component
  useEffect(() => {
    fetchSuggestions();
  }, []);

  // Only show this component for admins if isAdmin is true
  if (isAdmin === false) return null;

  return (
    <div className={`${theme.card.base} bg-opacity-5 overflow-hidden p-4 mb-6`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#0C3B2E]">{t('suggestions.title')}</h2>
        <button 
          onClick={() => { resetForm(); setShowForm(!showForm); }} 
          className={`${theme.button.base} ${theme.button.primary} ${theme.button.sizes.md}`}
        >
          {showForm ? t('cancel') : t('suggestions.addNew')}
        </button>
      </div>
      
      {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
        <p>{error}</p>
      </div>}
      
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white shadow-md rounded p-6 mb-6">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('suggestions.name')}
            </label>
            <input 
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="text" 
              placeholder={t('suggestions.namePlaceholder')}
              value={currentSuggestion.name}
              onChange={(e) => setCurrentSuggestion({...currentSuggestion, name: e.target.value})}
              required
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('suggestions.icon')}
            </label>
            <div className="space-y-2">
              <input 
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                type="text" 
                placeholder="icon-name.svg"
                value={currentSuggestion.icon}
                onChange={(e) => setCurrentSuggestion({...currentSuggestion, icon: e.target.value})}
                required
              />
              <p className="text-sm text-gray-500">
                Enter the SVG filename (with or without .svg extension). The file should be placed in the /public/card-icons/ directory.
              </p>

              {currentSuggestion.icon && (
                <div className="mt-4 p-4 border rounded">
                  <label className="block text-gray-700 text-sm font-bold mb-2">Preview:</label>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 border rounded p-2 bg-gray-50">
                      <img 
                        src={`/card-icons/${currentSuggestion.icon}`} 
                        alt="Icon preview"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>';
                        }}
                      />
                    </div>
                    <span className="text-sm text-gray-600">
                      {currentSuggestion.icon}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-end">
            <button 
              type="submit" 
              className={`${theme.button.base} ${theme.button.primary} ${theme.button.sizes.md}`}
              disabled={isLoading}
            >
              {isLoading ? t('processing') : currentSuggestion.originalKey ? t('update') : t('create')}
            </button>
          </div>
        </form>
      )}
      
      {isLoading && !suggestions.length ? (
        <div className="bg-white p-6 text-center">{t('loading')}</div>
      ) : suggestions.length === 0 ? (
        <div className="bg-white p-6 text-center">{t('suggestions.empty')}</div>
      ) : (
        <div className="bg-white overflow-hidden border shadow rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('suggestions.name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('suggestions.icon')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {suggestions.map(suggestion => (
                  <tr key={suggestion.key} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{suggestion.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center">
                          <img src={suggestion.icon} alt="icon" className="h-6 w-6" />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => editSuggestion(suggestion)}
                        className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.sm} mr-2`}
                      >
                        {t('edit')}
                      </button>
                      <button
                        onClick={() => deleteSuggestion(suggestion)}
                        className={`${theme.button.base} text-white bg-red-600 hover:bg-red-700 ${theme.button.sizes.sm}`}
                      >
                        {t('delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestionPictogramManager;